import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useAssessmentsAccess } from '@/hooks/feature-access/useAssessmentsAccess'
import { useMandatoryAssessmentRequired } from '@/hooks/useMandatoryAssessmentRequired'
import { useNeedsAnalysisStatus } from '@/hooks/useNeedsAnalysisStatus'
import { useOrgGroupAccess } from '@/hooks/useOrgGroupAccess'
import { useRole } from '@/hooks/useRole'
import { useUserpilotSignOutReset } from '@/hooks/useUserpilotSignOutReset'
import { formatRoleName } from '@/lib/utils'
import { ClerkUserRole } from '@/types/auth'
import { FeatureAccessPath } from '@/types/configuration'
import { toIso6391LanguageCode } from '@/utils/languageCodeUtils'
import { isEligibilityExpired, trialWindowDaysRemaining, trialWindowEndDate } from '@/utils/trialFormatters'
import { useClerk, useUser } from '@clerk/clerk-react'
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Userpilot } from 'userpilot'

// Where a learner is in the app-owned onboarding sequence, so Userpilot content
// waits its turn instead of stacking on the app's own modals:
// - needs_analysis: the needs analysis modal is still pending
// - assessment: a mandatory assessment is overdue, so its modal is showing
// - ready: nothing app-owned is blocking; tours and checklists may run
type OnboardingStage = 'needs_analysis' | 'assessment' | 'ready'

const SIGNIN_TRACKED_SESSIONS_KEY = 'userpilot:tracked_signin_sessions'

function hasTrackedSignIn(sessionId: string): boolean {
  try {
    const raw = localStorage.getItem(SIGNIN_TRACKED_SESSIONS_KEY)
    return raw ? raw.split(',').includes(sessionId) : false
  } catch {
    return false
  }
}

function markSignInTracked(sessionId: string): void {
  try {
    const raw = localStorage.getItem(SIGNIN_TRACKED_SESSIONS_KEY)
    const ids = raw ? raw.split(',') : []
    if (!ids.includes(sessionId)) {
      ids.push(sessionId)
      localStorage.setItem(SIGNIN_TRACKED_SESSIONS_KEY, ids.slice(-5).join(','))
    }
  } catch {
    // localStorage unavailable (private mode, etc.) — fail open and skip dedup
  }
}

/**
 * Identifies the current user in Userpilot. The authentication method is read
 * from the DB (`last_sign_in_method`, populated by the Clerk session.created
 * webhook and the LTI launch endpoint) so that Userpilot and the admin UI
 * always show the same value. Impersonation is signaled separately via the
 * `is_impersonation` boolean rather than overwriting the user's real method.
 *
 * Also emits a `user_signed_in` track event once per Clerk session so
 * Userpilot's event-based dashboards can chart sign-in activity. Dedup is
 * keyed by `clerk.session.id` in localStorage so a full page reload or a
 * second tab during the same session does not re-emit.
 */
export function useUserpilotIdentify(): void {
  // First, so its unsuppress runs before the identify effect below on sign-in.
  useUserpilotSignOutReset()
  const { user } = useUser()
  const clerk = useClerk()
  const { user: userDetails } = useCurrentUser()
  const groupName = userDetails?.group_name
  const { userRole } = useRole()
  const { i18n } = useTranslation()
  // The language the app chrome is actually rendering in, so tours match it.
  const localeCode = toIso6391LanguageCode(i18n.resolvedLanguage ?? i18n.language)

  // A/B experiment cohort for the homepage Featured Lessons row (ENG-2823).
  // Sourced from Unleash so assignment stays consistent with what the user sees.
  const featuredLessonsEnabled = useFlag('homepage-featured-lessons')
  const { flagsReady } = useFlagsStatus()

  const {
    hasCompletedNeedsAnalysis,
    error: needsAnalysisError,
    isLoading: isNeedsAnalysisLoading,
  } = useNeedsAnalysisStatus()
  const {
    shouldShowMandatoryAssessment,
    assessmentDueAt,
    isLoading: isAssessmentLoading,
  } = useMandatoryAssessmentRequired()
  const onboardingReady = !isNeedsAnalysisLoading && !isAssessmentLoading
  // Mirrors the guards: only learners are prompted, so everyone else is 'ready'.
  // A failed status lookup shows no modal (NeedsAnalysisGuard lets the learner
  // through), so it must not hold Userpilot content back either.
  let onboardingStage: OnboardingStage = 'ready'
  if (userRole === ClerkUserRole.LEARNER && !needsAnalysisError && !hasCompletedNeedsAnalysis)
    onboardingStage = 'needs_analysis'
  else if (shouldShowMandatoryAssessment) onboardingStage = 'assessment'
  const assessmentDueAtIso = assessmentDueAt?.toISOString() ?? null

  // What this learner can actually use, resolved the same way as the nav and the
  // homepage quick links, so onboarding content (e.g. a checklist variant with a
  // group class task) can target real access instead of CRM fields.
  const { hasAccess: groupClassesEnabled, isLoading: isGroupClassesLoading } = useOrgGroupAccess({
    configPath: FeatureAccessPath.LIVE_SESSIONS_GROUP_CLASSES_ENABLED,
    restrictedRoles: [ClerkUserRole.LEARNER, ClerkUserRole.COACH],
  })
  const { isEnabled: assessmentsEnabled, isLoading: isAssessmentsAccessLoading } = useAssessmentsAccess({
    context: 'learner',
  })
  // The assessments check includes an Unleash flag, so it also waits on flags.
  const accessReady = !isGroupClassesLoading && !isAssessmentsAccessLoading && flagsReady

  useEffect(() => {
    // Wait for group name if user has a group_id
    const groupLoading = userDetails?.group_id && groupName === undefined

    if (user && !groupLoading) {
      const isImpersonation = !!clerk.session?.actor
      const authMethod = userDetails?.last_sign_in_method ?? null
      const trial = userDetails?.trial_summary

      Userpilot.identify(user.id, {
        name: user.fullName,
        email: user.primaryEmailAddress?.emailAddress,
        created_at: user.createdAt?.toISOString(),
        // Native language as its canonical English name. Not a locale: live
        // survey segments target it by name ("Spanish", "Farsi"), so keep it.
        language: userDetails?.learner_info?.native_language,
        // Userpilot's localization parameter (ISO 639-1). Distinct from the
        // native language above, and from the auto-collected browser_language,
        // which reflects the device (the admin's, when impersonating).
        locale_code: localeCode,
        proficiency_level: userDetails?.learner_info?.level,
        role: userRole,
        role_name: formatRoleName(userRole),
        group_id: userDetails?.group_id,
        group_name: groupName,
        // Trial standing. ac_status gates the in-app AC pop-ups (show
        // requirements while 'unmet', hide once 'met'). All undefined for
        // non-trial users, who have no trial_summary.
        ac_status: trial?.ac_status,
        // 'expired' once the trial ended with eligibility never resolved, matching
        // what the eligibility badge shows: a segment targeting learners awaiting
        // review must not keep messaging someone whose verdict can no longer change.
        eligibility_status: trial
          ? isEligibilityExpired(trial.eligibility_status, trial.trial_state)
            ? 'expired'
            : trial.eligibility_status
          : undefined,
        trial_state: trial?.trial_state,
        // Resolved through the trial-window helpers: converting overwrites the
        // enrollment's end date with the extended program end, so the raw values
        // stop describing the trial. Explicitly null once converted, because an
        // undefined property is dropped from the payload and would leave the
        // learner's last countdown standing in Userpilot forever.
        trial_end_date: trial
          ? trialWindowEndDate({
              trial_state: trial.trial_state,
              end_date: trial.trial_end_date,
              converted_at: trial.converted_at,
            })
          : undefined,
        trial_days_remaining: trial ? trialWindowDaysRemaining(trial) : undefined,
        // Drives "2 lessons to go" nudges. Explicitly null once the trial is no
        // longer active, so a stale count can't keep a nudge showing.
        ac_lessons_to_go: trial ? (trial.ac_lessons_to_go ?? null) : undefined,
        // Deliberately the raw instant, unlike the ET business dates above.
        trial_converted_at: trial?.converted_at,
        license_end_date: userDetails?.license_end_date,
        // Null without a current enrollment, so a stale start cannot keep a day-N flow targeted.
        enrollment_start_date: userDetails ? (userDetails.current_enrollment_start_date ?? null) : undefined,
        last_authentication_method: authMethod,
        is_impersonation: isImpersonation,
        // Only stamp the cohort once flags have loaded — otherwise treatment users
        // would be mislabeled 'control' during the brief Unleash load window. The
        // effect re-runs when flagsReady flips, backfilling the property.
        ...(flagsReady && {
          featured_lessons_experiment: featuredLessonsEnabled ? 'treatment' : 'control',
        }),
        // Stamped only once both lookups resolve, so a loading render can't
        // briefly report 'ready' and release a tour on top of a pending modal.
        // assessment_due_at is explicitly null once none is owed, clearing any
        // earlier value (an undefined property would be dropped and left stale).
        ...(onboardingReady && {
          onboarding_stage: onboardingStage,
          assessment_due_at: assessmentDueAtIso,
        }),
        ...(accessReady && {
          group_classes_enabled: groupClassesEnabled,
          assessments_enabled: assessmentsEnabled,
        }),
        company: {
          id: user.organizationMemberships?.[0]?.organization.id,
          name: user.organizationMemberships?.[0]?.organization.name,
          slug: user.organizationMemberships?.[0]?.organization.slug,
          created_at: user.organizationMemberships?.[0]?.organization.createdAt?.toISOString(),
        },
      })

      const sessionId = clerk.session?.id
      if (sessionId && userDetails && !hasTrackedSignIn(sessionId)) {
        Userpilot.track('user_signed_in', {
          authentication_method: authMethod,
          is_impersonation: isImpersonation,
        })
        markSignInTracked(sessionId)
      }
    }
  }, [
    user,
    userRole,
    userDetails,
    groupName,
    clerk,
    featuredLessonsEnabled,
    flagsReady,
    localeCode,
    onboardingReady,
    onboardingStage,
    assessmentDueAtIso,
    accessReady,
    groupClassesEnabled,
    assessmentsEnabled,
  ])
}
